import React from 'react';
import T from 'prop-types';
// components
import ToggleSwitcher from 'components/ToggleSwitcher';
import Collapse from 'components/Collapse/Collapse';
import Button from 'components/Button';
import Input from 'components/Input';
import { FormattedMessage } from 'react-intl';

const PeriodBlock = ({ days, settings, setSettings, updateSettings }) => {
  const enabledKey = `mortality_trend_${days}_enabled`;
  const thresholdKey = `mortality_trend_${days}_threshold_percent`;
  const newThresholdKey = `new_${thresholdKey}`;
  const hardCountKey = `mortality_trend_${days}_hard_count`;
  const newHardCountKey = `new_${hardCountKey}`;

  const types = [
    { key: 'acute', label: 'Acute' },
    { key: 'chronic', label: 'Chronic' },
    { key: 'euthanasia', label: 'Euthanized' },
    { key: 'treated', label: 'Treated' },
  ];

  const setNumeric = (key) => (e) => {
    e.persist();
    // allow decimals like .5, 0.75, 1.25; keep at most 2 decimal places
    const reg = /^(?:\d{0,3}(?:\.\d{0,2})?|(?:\.\d{0,2}))$/;
    const { value } = e.target;
    if (value === '') {
      setSettings({ [key]: value });
      return;
    }
    if (!reg.test(value)) return;

    // normalize leading zeros for integer part, but keep "0." and leading dot cases
    let normalized = value;
    if (/^0[0-9]/.test(normalized)) {
      normalized = normalized.replace(/^0+/, '');
    }

    const num = parseFloat(normalized);
    if (Number.isNaN(num)) {
      setSettings({ [key]: normalized });
      return;
    }
    if (num > 100) {
      setSettings({ [key]: 100 });
      return;
    }
    setSettings({ [key]: normalized });
  };

  const setInteger = (key) => (e) => {
    e.persist();
    const reg = /^\d{0,6}$/g;
    const { value } = e.target;
    if (value === '') {
      setSettings({ [key]: value });
      return;
    }
    if (!reg.test(value)) return;
    const num = parseInt(value, 10);
    if (Number.isNaN(num)) return;
    setSettings({ [key]: num });
  };

  const handleToggle = ({ target: { name, checked } }) => updateSettings({ [name]: checked });

  // Save for percent thresholds (0-100), allow decimals
  const handleSavePercent = (originalKey, newKey) => () => {
    let v = parseFloat(settings[newKey] || 0);
    if (Number.isNaN(v)) v = 0;
    if (v > 100) v = 100;
    if (v < 0) v = 0;
    // round to 2 decimals to avoid float artifacts
    v = Math.round(v * 100) / 100;
    updateSettings({ [originalKey]: v });
  };

  // Save for integer limits (no upper bound, non-negative)
  const handleSaveInteger = (originalKey, newKey) => () => {
    let v = parseInt(settings[newKey] || 0, 10);
    if (v < 0) v = 0;
    updateSettings({ [originalKey]: v });
  };

  return (
    <div className="mt-15">
      <ToggleSwitcher
        name={enabledKey}
        className="small-12"
        text={<b><FormattedMessage id="component.mortalityTrendSettings.periodTitle" values={{ days }} /></b>}
        checked={!!settings[enabledKey]}
        onChange={handleToggle}
      >
        <div className="mt-10">
          <div className="mb-5 mts-subtitle">
            <FormattedMessage id="component.mortalityTrendSettings.mortalityThreshold" /> (%)
          </div>
          <div className="withdrawal inline-row">
            <div className="fixed-input-box">
              <Input
                type="text"
                value={settings[newThresholdKey] || 0}
                onChange={setNumeric(newThresholdKey)}
                disabled={!settings[enabledKey]}
                hasNoMargin
              />
            </div>
            {settings[enabledKey] && (
              <Button
                primary
                className="save-weight-btn ml-10"
                onClick={handleSavePercent(thresholdKey, newThresholdKey)}
                disabled={Number(settings[thresholdKey] || 0) === Number(settings[newThresholdKey] || 0)}
              >
                <FormattedMessage id="general.button.save" />
              </Button>
            )}
          </div>
          <div className="mb-5 mt-10 mts-subtitle">
            <FormattedMessage id="component.mortalityTrendSettings.hardCount" />
          </div>
          <div className="withdrawal inline-row">
            <div className="fixed-input-box">
              <Input
                type="text"
                value={settings[newHardCountKey] || 0}
                onChange={setInteger(newHardCountKey)}
                disabled={!settings[enabledKey]}
                hasNoMargin
              />
            </div>
            {settings[enabledKey] && (
              <Button
                primary
                className="save-weight-btn ml-10"
                onClick={handleSaveInteger(hardCountKey, newHardCountKey)}
                disabled={Number(settings[hardCountKey] || 0) === Number(settings[newHardCountKey] || 0)}
              >
                <FormattedMessage id="general.button.save" />
              </Button>
            )}
          </div>
          <Collapse
            className="mt-15"
            title={(
              <span className="mts-section-title">
                <b><FormattedMessage id="component.mortalityTrendSettings.deathTypeLimits" /></b>
              </span>
            )}
          >
            {types.map(({ key, label }) => {
              const enableKey = `mortality_trend_${days}_${key}_enabled`;
              const limitKey = `mortality_trend_${days}_${key}_limit`;
              const newLimitKey = `new_${limitKey}`;
              return (
                <ToggleSwitcher
                  key={key}
                  name={enableKey}
                  className="small-12"
                  text={label}
                  checked={!!settings[enableKey]}
                  onChange={handleToggle}
                >
                  <div className="withdrawal inline-row">
                    <div className="fixed-input-box">
                      <Input
                        type="text"
                        value={settings[newLimitKey] || 0}
                        onChange={setInteger(newLimitKey)}
                        disabled={!settings[enableKey]}
                        hasNoMargin
                      />
                    </div>
                    {settings[enableKey] && (
                      <Button
                        primary
                        className="save-weight-btn ml-10"
                        onClick={handleSaveInteger(limitKey, newLimitKey)}
                        disabled={Number(settings[limitKey] || 0) === Number(settings[newLimitKey] || 0)}
                      >
                        <FormattedMessage id="general.button.save" />
                      </Button>
                    )}
                  </div>
                </ToggleSwitcher>
              );
            })}
          </Collapse>
        </div>
      </ToggleSwitcher>
    </div>
  );
};

const MortalityTrendSettings = ({ tenant_settings, setSettings, updateSettings }) => {
  return (
    <div className="mt-10">
      {[2, 3, 5, 7].map((days) => (
        <PeriodBlock
          key={days}
          days={days}
          settings={tenant_settings}
          setSettings={setSettings}
          updateSettings={updateSettings}
        />
      ))}
    </div>
  );
};

MortalityTrendSettings.propTypes = {
  tenant_settings: T.object.isRequired,
  setSettings: T.func.isRequired,
  updateSettings: T.func.isRequired,
};

PeriodBlock.propTypes = {
  days: T.number.isRequired,
  settings: T.object.isRequired,
  setSettings: T.func.isRequired,
  updateSettings: T.func.isRequired,
};

export default MortalityTrendSettings;
